import { useEffect, useRef, useState, type RefObject } from 'react';

import { useSelection } from './useSelection.js';
import { useValueNavigation } from '../text-spin-button/hooks/useValueNavigation.js';
import {
  type TextSpinButtonProps,
  type TextSpinButtonRef,
} from '../text-spin-button/types.js';

/**
 * This hook provides text selection and cursor utility functions.
 * Because the SpinButton is a contenteditable span, this is not provided natively.
 * @internal
 */
export const useTextSpinButton = (
  props: Pick<
    TextSpinButtonProps,
    | 'required'
    | 'hasError'
    | 'disabled'
    | 'readOnly'
    | 'value'
    | 'options'
    | 'onChange'
  >,
): Pick<
  TextSpinButtonProps,
  | 'required'
  | 'hasError'
  | 'disabled'
  | 'readOnly'
  | 'value'
  | 'options'
  | 'onKeyDownHandler'
> & {
  ref: RefObject<TextSpinButtonRef | null> | null;
  onChange: (value: string | number | null) => void;
} => {
  const { disabled, readOnly, required, hasError, value, options, onChange } =
    props;

  const ref = useRef<TextSpinButtonRef>(null);

  const { selectAll } = useSelection(ref.current?.element);

  const [internalValue, setInternalValue] = useState<string | null | undefined>(
    value,
  );

  const [internalError, setInternalError] = useState<boolean | undefined>(
    hasError,
  );

  const { onKeyDownHandler, searchValue } = useValueNavigation({
    value: internalValue,
    disabled,
    options,
    readOnly,
    onChange: (value) => {
      if (value !== internalValue) {
        setInternalValue(value);
      }
    },
    required,
  });

  // set error state on value change
  useEffect(() => {
    if (required && (internalValue === null || internalValue === undefined)) {
      setInternalError(true);
    } else {
      setInternalError(
        internalValue !== null &&
          internalValue !== undefined &&
          !options.includes(internalValue),
      );
    }
  }, [internalValue, required, value, options]);

  useEffect(() => {
    if (!ref.current?.element?.current) {
      return;
    }

    searchValue?.(internalValue?.[0] ?? '');
    if (ref.current?.onChange) {
      ref.current.onChange(internalValue ?? '');
    }

    document.activeElement === ref.current?.element?.current && selectAll();
    // no need to trigger on selectAll update, only trigger on value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue]);

  return {
    value: internalValue,
    options,
    hasError: internalError,
    onKeyDownHandler,
    required,
    readOnly,
    disabled,
    ref,
    onChange: (value) => {
      onChange?.(value?.toString() ?? '');
      if (value === null) {
        value !== internalValue && setInternalValue(value);
        return;
      }
      value !== internalValue &&
        setInternalValue(typeof value === 'number' ? value.toString() : value);
    },
  };
};
